import {type CommerceStateShape} from '@packages/systems/commerce/store';
import {dynamoPageSlugPrefix} from '@packages/systems/core/constants/SharedConfig';
import PageNode, {
  type PageRecordType,
  type PageState,
  type PageLocalizedSettingsForLocale,
  DynamicPagesType,
  StaticPagesType,
  ComponentPagesType,
} from '@packages/systems/core/records/PageRecord';
import {
  isMint,
  makeMintId,
  uniqueSlug,
} from '@packages/systems/core/utils/RecordUtils';
import {LocaleUtils} from '@packages/systems/localization';
import type {Locale} from '@packages/systems/localization-types';
import {USYS_RESERVED_SLUGS} from '@packages/systems/users/constants';
import {
  getIsSiteUsersActivated,
  type SiteUsersState,
} from '@packages/systems/users/flux';
import {get, getElse} from '@packages/utilities/data-utils';
import {isNil} from '@packages/utilities/fp/utils';
import {weakMemo, singleMemo} from '@packages/utilities/memo';
import {reportBug} from '@packages/utilities/reporter';
import {List, Map as IMap, Seq} from 'immutable';
/**
 * For working with the state managed by the PageStore as well as lists of
 * PageNodes in bulk.
 */
import {curry, flowRight, identity, pick, startsWith} from 'lodash';
import {createSelector} from 'reselect';
import {
  isFolder,
  isHomePage,
  isPage,
  isBranchPage,
  isStaticPage,
  isUtilityPage,
  removeDynamoSlug,
} from './PageUtils';

type PageDefinition = typeof UTILITY_PAGE_DEFAULTS & {
  slug: PageRecordType['slug'];
  utilKey: PageRecordType['utilKey'];
};

const UTILITY_PAGE_DEFAULTS = {
  seoDesc: '',
  ogTitle: '',
  ogTitleCopied: true,
  ogDesc: '',
  ogDescCopied: true,
  generateRss: null,
  rssTitle: '',
  rssDesc: '',
  rssLink: null,
  rssTtl: null,
  rssItemTitle: '',
  rssItemTitleCopied: true,
  rssItemDesc: '',
  rssItemDescCopied: true,
  rssItemImage: null,
  searchExclude: true,
  searchTitle: '',
  searchTitleCopied: true,
  searchDescription: '',
  searchDescriptionCopied: true,
  searchImage: '',
  searchImageCopied: true,
  head: '',
  postBody: '',
  __v: 0,
  archived: false,
  ogImages: [],
  domCount: 0,
  domOps: 0,
  type: 'page',
  parent: null,
  draft: false,
  includeInSitemap: true,
  shouldPublish: true,
} as const;

export const DEFAULT_UTILITY_PAGE_DEFINITIONS: Array<PageDefinition> = [
  {
    ...UTILITY_PAGE_DEFAULTS,
    // @ts-expect-error - TS2322 - Type '{ _id: string; slug: string; utilKey: "401"; title: string; name: string; type: "page"; seoTitle: string; draft: false; seoDesc: ""; ogTitle: ""; ogTitleCopied: true; ogDesc: ""; ogDescCopied: true; generateRss: null; ... 25 more ...; shouldPublish: true; }' is not assignable to type 'PageDefinition'.
    _id: makeMintId(),
    //site: ...
    //createdOn: ...
    slug: '401',
    utilKey: '401',
    title: 'Password',
    name: 'Password',
    type: 'page',
    seoTitle: 'Protected page',
    draft: false,
  },
  {
    ...UTILITY_PAGE_DEFAULTS,
    // @ts-expect-error - TS2322 - Type '{ _id: string; slug: string; utilKey: "search"; title: string; name: string; type: "page"; seoTitle: string; draft: false; seoDesc: ""; ogTitle: ""; ogTitleCopied: true; ogDesc: ""; ogDescCopied: true; ... 26 more ...; shouldPublish: true; }' is not assignable to type 'PageDefinition'.
    _id: makeMintId(),
    //site: ...
    //createdOn: ...
    slug: 'search',
    utilKey: 'search',
    title: 'Search Results',
    name: 'Search Results',
    type: 'page',
    seoTitle: 'Search Results',
    draft: false,
  },
  {
    ...UTILITY_PAGE_DEFAULTS,
    // @ts-expect-error - TS2322 - Type '{ _id: string; slug: string; utilKey: "404"; title: string; name: string; type: "page"; seoTitle: string; draft: false; seoDesc: ""; ogTitle: ""; ogTitleCopied: true; ogDesc: ""; ogDescCopied: true; generateRss: null; ... 25 more ...; shouldPublish: true; }' is not assignable to type 'PageDefinition'.
    _id: makeMintId(),
    //site: ...
    //createdOn: ...
    slug: '404',
    utilKey: '404',
    title: '404',
    name: '404',
    type: 'page',
    seoTitle: 'Not Found',
    draft: false,
  },
  {
    ...UTILITY_PAGE_DEFAULTS,
    // @ts-expect-error - TS2322 - Type '{ _id: string; slug: string; utilKey: "ecommerce-checkout"; title: string; name: string; type: "page"; seoTitle: string; draft: false; seoDesc: ""; ogTitle: ""; ogTitleCopied: true; ogDesc: ""; ogDescCopied: true; ... 26 more ...; shouldPublish: true; }' is not assignable to type 'PageDefinition'.
    _id: makeMintId(),
    //site: ...
    //createdOn: ...
    slug: 'checkout',
    utilKey: 'ecommerce-checkout',
    title: 'Checkout',
    name: 'Checkout',
    type: 'page',
    seoTitle: 'Checkout',
    draft: false,
  },
  {
    ...UTILITY_PAGE_DEFAULTS,
    // @ts-expect-error - TS2322 - Type '{ _id: string; slug: string; utilKey: "ecommerce-paypal-checkout"; title: string; name: string; type: "page"; seoTitle: string; draft: false; seoDesc: ""; ogTitle: ""; ogTitleCopied: true; ogDesc: ""; ... 27 more ...; shouldPublish: true; }' is not assignable to type 'PageDefinition'.
    _id: makeMintId(),
    //site: ...
    //createdOn: ...
    slug: 'paypal-checkout',
    utilKey: 'ecommerce-paypal-checkout',
    title: 'Checkout (PayPal)',
    name: 'Checkout (PayPal)',
    type: 'page',
    seoTitle: 'Checkout (PayPal)',
    draft: false,
  },
  {
    ...UTILITY_PAGE_DEFAULTS,
    // @ts-expect-error - TS2322 - Type '{ _id: string; slug: string; utilKey: "ecommerce-confirmation"; title: string; name: string; type: "page"; seoTitle: string; draft: false; seoDesc: ""; ogTitle: ""; ogTitleCopied: true; ogDesc: ""; ogDescCopied: true; ... 26 more ...; shouldPublish: true; }' is not assignable to type 'PageDefinition'.
    _id: makeMintId(),
    //site: ...
    //createdOn: ...
    slug: 'order-confirmation',
    utilKey: 'ecommerce-confirmation',
    title: 'Order Confirmation',
    name: 'Order Confirmation',
    type: 'page',
    seoTitle: 'Order Confirmation',
    draft: false,
  },
];

export const UTILITY_PAGE_SLUGS: Array<string> =
  DEFAULT_UTILITY_PAGE_DEFINITIONS.map(({slug}) => slug);

const didTheUserSetUpCommerceForThisSite = (
  commerceState: CommerceStateShape
) =>
  commerceState && commerceState.status
    ? commerceState.status.cmsState === 'ready'
    : false;
///
// Constants.
///

// A node inserted into the pages table to simplify PageTree traversals.
export const ROOT_NODE = PageNode({id: '__ROOT__'});

const PAGE_NODE_KEYS = PageNode().keySeq().toArray();

// Need to include utility page slugs to make it impossible to make a folder
// with a utility slug for a name
const RESERVED_SLUGS = [
  'images',
  'css',
  'js',
  'index',
  'manifest',
  'static-manifest',
  'page-defaults',
].concat(UTILITY_PAGE_SLUGS);

///
// State selectors.
//
// For selecting specific slices or kinds of data within the PageStore. Unless
// noted otherwise, each takes a single `pageState` argument which represents
// the PageStore's entire state blob.
///

export const getPageNodes = weakMemo(
  (pageState: PageState): IMap<string, PageRecordType> =>
    get('staticPages', pageState).concat(
      get('dynamicPages', pageState).concat(get('componentPages', pageState))
    )
);

export const getPageTreeNodes: (pageState: PageState) => StaticPagesType =
  get('staticPages');

export const getDynPages: (pageState: PageState) => DynamicPagesType =
  get('dynamicPages');

export const getComponentPages: (pageState: PageState) => ComponentPagesType =
  get('componentPages');

export const getPage = (
  pageId: string | null | undefined,
  pageState: PageState
): PageRecordType | null => getElse(pageId, null, getPages(pageState));

export const getPages = weakMemo((pageState: PageState) =>
  getPageNodes(pageState).filter(isPage)
);

export const getAllPageIds = (pageState: PageState) =>
  getPages(pageState)
    .map((page) => page.id)
    .toArray();

export const getStaticPages = weakMemo((pageState: PageState) =>
  getPageTreeNodes(pageState).filter(isStaticPage)
);

export const getHomePage = (pageState: PageState) =>
  getPageTreeNodes(pageState).find(isHomePage);

export const getPageBySlug = (pageState: PageState, slug: string) =>
  getPageTreeNodes(pageState).find((page) => page.slug === slug);

export const getPageByUtilKey = (pageState: PageState, utilKey: string) =>
  getPageTreeNodes(pageState).find((page) => page.utilKey === utilKey);

export const getSearchResultPage = (pageState: PageState) =>
  getPageByUtilKey(pageState, 'search');

export const getCheckoutPaypalPage = (pageState: PageState) =>
  getPageByUtilKey(pageState, 'ecommerce-paypal-checkout');

// ENT-1008: we're returning an object with one branch and one page, because one
// collection ID will either have a dynamic page or a dynamic page and a dynamic
// page branch.
// If we get more than two pages back at this time, we want to know, so we'll log a
// warning.
export const getDynPageAndBranchByCollectionId = (
  pageState: PageState,
  collectionId: string
) => {
  const dynPageMap = getDynPages(pageState).filter(
    (page) => page.dynCollection === collectionId
  );
  if (dynPageMap.size > 2) {
    reportBug(
      'DynamicPageMapError',
      'Returned more than a single branch/page combination for collection',
      {
        team: 'enterprise',
        ticket: 'https://webflow.atlassian.net/browse/ENT-1107',
        priority: 'warning',
        collection: collectionId,
      }
    );
  }
  const page = dynPageMap.find((p) => !p.branchId);
  // @ts-expect-error - TS7006 - Parameter 'p' implicitly has an 'any' type.
  const branch = dynPageMap.find((p) => p.branchId);
  return {page, branch};
};

export const hasNonMintSearchResultPage = weakMemo((pageState: PageState) => {
  const searchResultsPage = getSearchResultPage(pageState);
  return Boolean(searchResultsPage) && !isMint(searchResultsPage);
});

export const getRootNode = get(ROOT_NODE.id);

///
// Queries and derived state.
///

export type PageTreeNodes = IMap<string, PageRecordType>;
/**
 * Builds a path of ancestors from the node with the provided `id` to the root
 * of the PageTree, including the node itself. The returned list is ordered
 * from leaf -> root.
 *
 * @param  {string} id
 * @param  {Immutable.Map.<string, PageRecordType>} pageTreeNodes
 * @return {Immutable.List.<PageRecordType>}
 */
export const nodeAncestry = (
  id: string,
  pageTreeNodes: PageTreeNodes
): List<PageRecordType> => {
  if (isNil(id)) return List();

  const node = get(id, pageTreeNodes);
  return List.of(node).concat(nodeAncestry(node.parentId, pageTreeNodes));
};

/**
 * Checks if a node with provided id contains current page,
 * returns `true` for all ancestors of current page
 *
 * @param  {string} id
 * @param  {string} currentPageId
 * @param  {Immutable.Map.<string, PageRecordType>} pageTreeNodes
 * @return {Boolean}
 */
export const containsCurrentPage = (
  id: string,
  currentPageId: string,
  pageTreeNodes: PageTreeNodes
) => {
  if (!pageTreeNodes.has(currentPageId)) {
    return false;
  }

  const currentPageAncestors: {id: string}[] = nodeAncestry(
    currentPageId,
    pageTreeNodes
  ).toArray();

  return currentPageAncestors.some((ancestor) => ancestor.id === id);
};

/**
 * Searches for node ancestors and returns an array of folder names,
 * doesn't include root
 */
export const getAncestorsFolderNames = (
  id: string,
  pageTreeNodes: PageTreeNodes
) => {
  return nodeAncestry(id, pageTreeNodes)
    .toArray()
    .filter((node: PageRecordType) => !isPage(node) && !isRoot(node))
    .map((node: PageRecordType) => node.name);
};

/**
 * Returns a list of all nodes which are nested under the node with the provided
 * `id`, including those nested within other folders.
 */
export const nodeDescendants = (
  id: string,
  pageTreeNodes: PageTreeNodes
): List<PageRecordType> => {
  if (isNil(id)) return List();

  const children = nodesWithParentId(id, pageTreeNodes);
  // @ts-expect-error - TS2740 - Type 'Iterable<number, PageRecordType>' is missing the following properties from type 'List<PageRecordType>': set, delete, remove, insert, and 30 more.
  return children.concat(
    children.flatMap((child) => nodeDescendants(child.id, pageTreeNodes))
  );
};

/**
 * Calculates a page/folder node's depth in the overall page tree structure.
 * This is used to indent node's at varying levels to create a visual tree.
 * Top level pages/folders have a depth of 1.
 *
 * Top Level Page (1)
 * Top Level Folder (1)
 * |__ Second Level Page (2)
 * |__ Second Level Folder (2)
 *    |__ Third Level Page (3)
 * Top Level Page (1)
 * Top Level Page (1)
 */
export function getNodeTreeDepth(
  id: string,
  pageTreeNodes: IMap<string, PageRecordType>
): number {
  if (id === ROOT_NODE.id) return 0;

  const node = get(id, pageTreeNodes);

  return getNodeTreeDepth(node.parentId, pageTreeNodes) + 1;
}

/**
 * Derives the full slug of the node with the provided `id`.
 *
 * Note: The slugs stored on the actual records are only the node's own
 * fragment, so this must be used to get fully qualified slugs.
 *
 * Note: This does not include any leading or trailing slashes - you need to add those yourself.
 */
export const fullSlug = (
  id: string,
  pageTreeNodes: PageTreeNodes,
  opts: {
    currentLocale?: Locale | null | undefined;
  } = {}
): string => {
  const ancestry = nodeAncestry(id, pageTreeNodes);

  const pathAfterLocaleSubdirectory: string = ancestry
    .filter((node) => {
      return (
        // Root nodes don't contribute to the slug
        node.id !== ROOT_NODE.id &&
        // Branch pages also don't contribute to the slug -- their parent node is the base page, which does
        !isBranchPage(node)
      );
    })
    // Ancestry is ordered from leaf -> root, but the slug is root -> leaf.
    .reverse()
    .map((node) => {
      const primarySlug = node.get('slug');
      const slug =
        opts.currentLocale && !opts.currentLocale.primary
          ? (node
              .get('localizedSettings')
              ?.get(opts.currentLocale._id)
              ?.get('slug') ?? primarySlug)
          : primarySlug;
      if (node.dynCollection) {
        /**
         * The slug for a base collection template page will have a "detail_"
         * prefix, which we trim to get the original slug.
         */
        return removeDynamoSlug(slug);
      }
      return slug;
    })
    .join('/');

  const localeSubdirectory: string = LocaleUtils.getSubdirectory(
    opts.currentLocale
  );
  return [localeSubdirectory, pathAfterLocaleSubdirectory]
    .filter((part) => Boolean(part))
    .join('/');
};

/**
 * Retrieves a sorted list of children for the node with the specified
 * `parentId`.
 *
 */
const _nodesWithParentId = (
  parentId: string,
  pageTreeNodes: PageTreeNodes
): Seq<number, PageRecordType> =>
  pageTreeNodes
    .valueSeq()
    .filter((node) => node.parentId === parentId)
    .sortBy(get('siblingIndex'));

export const nodesWithParentId = curry(_nodesWithParentId);

const _nodesSameTypeWithParentId = (
  parentId: string | null | undefined,
  type: string,
  pageTreeNodes: PageTreeNodes
): Seq<number, PageRecordType> =>
  pageTreeNodes
    .valueSeq()
    .filter((node) => node.parentId === parentId && node.type === type)
    .sortBy(get('siblingIndex'));

export const nodesSameTypeWithParentId = curry(_nodesSameTypeWithParentId);

export const getRootChildren = flowRight(
  nodesWithParentId(ROOT_NODE.id),
  getPageTreeNodes
);

/**
 * Returns true if the provided `node` is the root, else false.
 */
// @ts-expect-error - TS2749 - 'PageNode' refers to a value, but is being used as a type here. Did you mean 'typeof PageNode'? | TS2749 - 'PageListNode' refers to a value, but is being used as a type here. Did you mean 'typeof PageListNode'?
export const isRoot = (node: PageNode | PageListNode): boolean =>
  getElse('id', '', node) === ROOT_NODE.id;

/**
 * Returns true if the provided `node` parent is the root, else false.
 */
// @ts-expect-error - TS2749 - 'PageNode' refers to a value, but is being used as a type here. Did you mean 'typeof PageNode'?
export const parentIsRoot = (node: PageNode): boolean =>
  getElse('parentId', '', node) === ROOT_NODE.id;

///
// Transformation utilities.
///

/**
 * Creates a new PageNode using the properties provided as `node` and
 * returns the set of PageNode objects that results from its insertion. To
 * insert with a specific parent or in a specific position within a parent's
 * children, include the `parentId` and `siblingIndex` properties, respectively.
 *
 * @param  {Object|Immutable.KeyedIterable} props
 */
export const insertNode = (
  props: any,
  pageTreeNodes: PageTreeNodes
): PageTreeNodes => {
  const parentId = getElse('parentId', ROOT_NODE.id, props);
  const siblingIndex = getElse(
    'siblingIndex',
    // @ts-expect-error - TS2339 - Property 'count' does not exist on type 'CurriedFunction1<string, Seq<number, PageRecordType>>'.
    nodesWithParentId(parentId, pageTreeNodes).count(),
    props
  );

  // @ts-expect-error - TS2322 - Type 'CurriedFunction1<string, PageTreeNodes>' is not assignable to type 'PageTreeNodes'.
  return updateChildren(
    // @ts-expect-error - TS7006 - Parameter 'children' implicitly has an 'any' type.
    (children) => children.splice(siblingIndex, 0, PageNode(props)),
    parentId,
    pageTreeNodes
  );
};

/**
 * Updates the node at the given `id` by merging it with provided `props`, then
 * returns the updated set of PageNode objects.
 *
 * @param  {Object|Immutable.KeyedIterable} props
 */
export const updateNode = (
  id: string,
  props: any,
  pageTreeNodes: PageTreeNodes
): PageTreeNodes => {
  const node = get(id, pageTreeNodes);
  let updatedNode = node.merge(pick(props, PAGE_NODE_KEYS));

  // Resolve any slug conflicts if the folder's parent is changing.
  if (node.parentId !== updatedNode.parentId) {
    const nodesOfSameType = pageTreeNodes
      .delete(node.id)
      .filter((x) => x.type === node.type);
    updatedNode = updatedNode.set(
      'slug',
      makeUniqueSlug(updatedNode.slug, updatedNode.parentId, nodesOfSameType)
    );
  }

  return rebuildChildren(
    id,
    insertNode(updatedNode, deleteNode(id, pageTreeNodes))
  );
};

/**
 * Inserts a new PageNode using the provided `node` object as its properties
 * if an entry at `id` does not already exist, else updates the existing node.
 */
const _upsertNode = (
  node: PageRecordType,
  pageTreeNodes: PageTreeNodes
): PageTreeNodes =>
  pageTreeNodes.has(get('id', node))
    ? updateNode(get('id', node), node, pageTreeNodes)
    : insertNode(node, pageTreeNodes);

export const upsertNode = curry(_upsertNode);

/**
 * Returns a new copy of `pageTreeNodes` without the node specified by `id`.
 */
export const deleteNode = (
  id: string,
  pageTreeNodes: PageTreeNodes
): PageTreeNodes =>
  // @ts-expect-error - TS2322 - Type 'CurriedFunction1<string, PageTreeNodes>' is not assignable to type 'PageTreeNodes'.
  updateChildren(
    // @ts-expect-error - TS7006 - Parameter 'children' implicitly has an 'any' type. | TS7006 - Parameter 'c' implicitly has an 'any' type.
    (children) => children.filterNot((c) => c.id === id),
    get(id, pageTreeNodes).parentId,
    pageTreeNodes.delete(id)
  );

/**
 * Applies the provided `updater` to the children of the node associated with
 * `parentId` and then reconciles any changes to ensure that dependent data
 * does not fall out of sync. Returns an updated copy of `pageTreeNodes`.
 */
const _updateChildren = (
  updater: any,
  parentId: string,
  pageTreeNodes: PageTreeNodes
): PageTreeNodes =>
  updater(nodesWithParentId(parentId, pageTreeNodes))
    // @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
    .map((node) => node.merge({parentId}))
    // @ts-expect-error - TS7006 - Parameter 'nodes' implicitly has an 'any' type. | TS7006 - Parameter 'child' implicitly has an 'any' type.
    .reduce((nodes, child) => nodes.set(child.id, child), pageTreeNodes);

const updateChildren = curry(_updateChildren);
const rebuildChildren = updateChildren(identity);

///
// Slug utilities.
///

/**
 * Generates a slug using the provided `slug` as a base which is guaranteed not
 * to conflict with any existing slugs on resources of its type. Folders and
 * pages may share the same slug.
 */
export const makeUniqueSlug = (
  slug: string,
  parentId: string | null | undefined,
  pageTreeNodes: PageTreeNodes
  // @ts-expect-error - TS2769 - No overload matches this call.
): string => uniqueSlug(slug, nodesWithParentId(parentId, pageTreeNodes));

export const makeUniqueSlugForSameType = (
  slug: string,
  type: string,
  parentId: string | null | undefined,
  pageTreeNodes: PageTreeNodes
): string =>
  uniqueSlug(slug, nodesSameTypeWithParentId(parentId, type, pageTreeNodes));

// @ts-expect-error - TS2749 - 'PageNode' refers to a value, but is being used as a type here. Did you mean 'typeof PageNode'?
const isCommerceReserved = (slug: string, page: PageNode): boolean => {
  let reservedEcommerceSlugs = RESERVED_SLUGS;
  // and if the page is a folder at the root then product, category, and sku slugs should not be available
  if (isFolder(page) && parentIsRoot(page)) {
    reservedEcommerceSlugs = [...RESERVED_SLUGS, 'product', 'category', 'sku'];
  }
  // and slugs that start with `detail_` should not be available
  return (
    reservedEcommerceSlugs.includes(slug) ||
    startsWith(slug, dynamoPageSlugPrefix)
  );
};

// @ts-expect-error - TS2749 - 'PageNode' refers to a value, but is being used as a type here. Did you mean 'typeof PageNode'?
const isUserSystemsReserved = (slug: string, page: PageNode): boolean =>
  parentIsRoot(page) && USYS_RESERVED_SLUGS.includes(slug);

/**
 * Returns true if the provided `slug` is not reserved for internal Webflow use,
 * else false.
 */
export const slugIsReserved = (
  slug: string | null | undefined,
  page: PageRecordType,
  isCommerceEnabled: boolean,
  isUserSystemsEnabled: boolean
): boolean => {
  if (!slug) {
    return false;
  }
  return (
    (isCommerceEnabled && isCommerceReserved(slug, page)) ||
    (isUserSystemsEnabled && isUserSystemsReserved(slug, page)) ||
    // checkout, paypal-checkout and order-confirmation slugs (from UTILITY_PAGE_SLUGS) should not be available if ecommerce is not enabled
    RESERVED_SLUGS.filter(
      (slugName) =>
        slugName !== 'checkout' &&
        slugName !== 'order-confirmation' &&
        slugName !== 'paypal-checkout'
    ).includes(slug)
  );
};

/**
 * Returns a non-empty array of field keys (eg. [`slug`] or [`localizedSettings[localeId].slug`] or combined)
 *   if the provided node's slug is reserved (and isn't a utility page).
 */
export const pageKeysWithReservedInvalidSlugs = (
  page: PageRecordType,
  commerceState: CommerceStateShape | null = null,
  userSystemsState: SiteUsersState | null = null
): string[] => {
  const keys: string[] = [];
  if (isUtilityPage(page)) {
    return keys;
  }
  const isCommerceEnabled =
    commerceState !== null && didTheUserSetUpCommerceForThisSite(commerceState);
  const isUserSystemsEnabled =
    userSystemsState !== null && getIsSiteUsersActivated(userSystemsState);

  // check values under a primary locale
  if (
    slugIsReserved(
      page.get('slug'),
      page,
      isCommerceEnabled,
      isUserSystemsEnabled
    )
  ) {
    keys.push('slug');
  }

  // check values under each secondary locale
  page
    .get('localizedSettings')
    ?.forEach(
      (
        secondaryLocaleSettings: PageLocalizedSettingsForLocale,
        secondaryLocaleId: string
      ) => {
        if (
          slugIsReserved(
            secondaryLocaleSettings.get('slug'),
            page,
            isCommerceEnabled,
            isUserSystemsEnabled
          )
        ) {
          keys.push(`localizedSettings.${secondaryLocaleId}.slug`);
        }
      }
    );

  return keys;
};

/**
 * Returns an array of conflicting keys if the provided node's slug (a regular one or one of localized ones)
 * conflicts with any other nodes of its type, else an empty array.
 */
export const keysOfConflictingSlugs = (
  node: PageRecordType,
  pageTreeNodes: PageTreeNodes,
  commerceState: CommerceStateShape | null = null
): string[] => {
  let nodesToCheck = pageTreeNodes;
  // If e-commerce module is not enabled then checkout, paypal checkout and order-confirmation slugs should be available.
  if (commerceState && !didTheUserSetUpCommerceForThisSite(commerceState)) {
    nodesToCheck = nodesToCheck.filterNot(
      (nodeInTree) =>
        nodeInTree.get('slug') === 'order-confirmation' ||
        nodeInTree.get('slug') === 'checkout' ||
        nodeInTree.get('slug') === 'paypal-checkout'
    );
  }

  const keys: Array<string> = [];
  // @ts-expect-error - TS2769 - No overload matches this call.
  nodesWithParentId(node.parentId, nodesToCheck)
    .filterNot((otherNode) => otherNode.id === node.id)
    .forEach((otherNode) => {
      if (otherNode.type !== node.type) {
        return;
      }
      if (otherNode.slug === node.slug) {
        keys.push('slug');
      }
      forEachSecondaryLocaleSettingsOf(
        node,
        otherNode,
        (
          secondaryLocaleSettings,
          otherSecondaryLocaleSettings,
          secondaryLocaleId
        ) => {
          const slug1 = secondaryLocaleSettings.get('slug') ?? node.slug;
          const slug2 =
            otherSecondaryLocaleSettings.get('slug') ?? otherNode.slug;
          if (slug1 === slug2) {
            keys.push(`localizedSettings.${secondaryLocaleId}.slug`);
            // It might be that conflict is on a secondary locale through derived primary locale slugs,
            // therefore we have to show an error for a primary slug as well.
            // It's not the best solution, but at least prevent a case when user cannot create/save
            // the page even though there are no errors shown.
            if (!keys.includes('slug')) {
              keys.push('slug');
            }
          }
        }
      );
    });
  return keys;
};

function forEachSecondaryLocaleSettingsOf(
  page1: PageRecordType,
  page2: PageRecordType,
  callback: (
    settings1: PageLocalizedSettingsForLocale,
    settings2: PageLocalizedSettingsForLocale,
    localeId: string
  ) => void
): void {
  const detectedLocaleIds = new Set<string>();
  page1.get('localizedSettings')?.forEach((_settings: any, localeId: any) => {
    detectedLocaleIds.add(localeId);
  });
  page2.get('localizedSettings')?.forEach((_settings: any, localeId: any) => {
    detectedLocaleIds.add(localeId);
  });

  detectedLocaleIds.forEach((localeId) => {
    const settings1 = page1.getIn(['localizedSettings', localeId]) ?? IMap();
    const settings2 = page2.getIn(['localizedSettings', localeId]) ?? IMap();
    // @ts-expect-error - type returned by getIn does not match PageLocalizedSettingsForLocale
    callback(settings1, settings2, localeId);
  });
}

/**
 * Returns a Number with a newly calculated siblingIndex depending
 * on the page target passed as a parameter
 */
export const siblingIndexFromTarget = (
  dropNode: PageRecordType,
  pageTreeNodes: PageTreeNodes,
  isAfter: boolean
): number => {
  // @ts-expect-error - TS2769 - No overload matches this call.
  const siblings = nodesWithParentId(dropNode.parentId, pageTreeNodes);
  if (dropNode.siblingIndex === null || dropNode.siblingIndex === undefined) {
    return 0;
  }
  // @ts-expect-error - TS2339 - Property 'indexOf' does not exist on type 'CurriedFunction2<string, PageTreeNodes, Seq<number, PageRecordType>> & CurriedFunction1<PageTreeNodes, Seq<number, PageRecordType>> & CurriedFunction1<...> & Seq<...>'.
  const dropTargetIndex = siblings.indexOf(dropNode);
  const companionIndex = isAfter ? dropTargetIndex + 1 : dropTargetIndex - 1; //The index of the companion element that will serve to calculate the average
  const isEdge = isAfter
    ? dropTargetIndex === siblings.count() - 1
    : dropTargetIndex === 0;
  const dropTargetCompanion = siblings.get(companionIndex);
  if (
    isEdge ||
    dropTargetCompanion.siblingIndex === null ||
    dropTargetCompanion.siblingIndex === undefined
  ) {
    return isAfter ? dropNode.siblingIndex + 1 : dropNode.siblingIndex - 1;
  }
  return (dropNode.siblingIndex + dropTargetCompanion.siblingIndex) / 2;
};

/**
 * Returns a Number with a newly calculated siblingIndex depending
 * on the folder target passed as a parameter
 */
export const siblingIndexFromFolderTarget = (
  dropNode: PageRecordType,
  pageTreeNodes: PageTreeNodes
): number => {
  const siblings = nodesWithParentId(dropNode.id, pageTreeNodes);
  if (
    siblings.count() === 0 ||
    siblings.get(0).siblingIndex === null ||
    siblings.get(0).siblingIndex === undefined
  ) {
    return 0;
  }
  return siblings.get(siblings.count() - 1).siblingIndex + 1; // Every element gets added to the last position of the folder
};

/**
 * Returns a new `siblingIndex` for a given parent node in the
 * page tree
 */
export const newSiblingIndex = (
  parentId: string,
  pageTreeNodes: PageTreeNodes
): number => {
  const siblings = _nodesWithParentId(parentId, pageTreeNodes);
  if (siblings.count() === 0) {
    return 0;
  }
  const lastNode = siblings.get(siblings.count() - 1);
  return lastNode?.siblingIndex + 1;
};

type GetProtectedAncestorArg = {
  topMostParent?: PageRecordType | null;
  pages: PageTreeNodes;
  page?: PageRecordType;
};

const _getProtectedAncestor = ({
  topMostParent = null,
  pages,
  page,
}: GetProtectedAncestorArg): PageRecordType | null => {
  if (!page) {
    return topMostParent;
  }

  const parentId = page.get('parentId');
  const parent = parentId ? pages.get(parentId) : null;
  if (!parent) {
    return topMostParent;
  }

  return _getProtectedAncestor({
    topMostParent:
      parent.get('passwordProtected') === true ||
      (parent.get('groupAccess')?.size ?? 0) > 0
        ? parent
        : topMostParent,
    pages,
    page: parent,
  });
};

type PageNodeAndPages = {
  pages: PageTreeNodes;
  page?: PageRecordType;
};
/**
 * Determine the topmost password-protected ancestor of a given `PageNode`.
 */
export const getProtectedAncestor = ({
  pages,
  page,
}: PageNodeAndPages): PageRecordType | null =>
  _getProtectedAncestor({
    pages,
    page,
  });

type FolderAndPages = {
  pages: PageTreeNodes;
  folder: PageRecordType;
};

/**
 * Retrieve a flat list of all protected items contained in a given folder (including nested items).
 * Useful when you need to know whose password will be overwritten when setting a password on a folder.
 */
export const getProtectedDescendants = ({
  pages,
  folder,
}: FolderAndPages): List<PageRecordType> => {
  const children = pages
    .filter((page) => page.get('parentId') === folder.get('id'))
    .toList();
  const unprotectedFolders = children.filter(
    (page) =>
      page.get('type') === 'Folder' &&
      page.get('passwordProtected') !== true &&
      (page.get('groupAccess')?.size ?? 0) <= 0
  );

  return unprotectedFolders.reduce(
    (protectedChildren, childFolder) =>
      protectedChildren.concat(
        getProtectedDescendants({
          pages,
          folder: childFolder,
          // The recursion here seems to make it difficult for TS to infer the type.
        }) as any
      ),
    children.filter(
      (page) =>
        page.get('passwordProtected') === true ||
        (page.get('groupAccess')?.size ?? 0) > 0
    )
  );
};

/**
 * Creates a map of page ids and hrefs with all the pages.
 */
export const generateHrefMap = weakMemo((pageState: PageState) =>
  singleMemo((locale: Locale | null | undefined): Record<string, string> => {
    const pageNodes = getPageNodes(pageState);
    return pageNodes
      .filter((page) => !isBranchPage(page))
      .map((page) => {
        const pageFullSlug = fullSlug(page.id, pageNodes, {
          currentLocale: locale,
        });
        return `/${pageFullSlug}`;
      })
      .toJS();
  })
);

export const selectCollectionIdToPageIdMap = createSelector(
  (pageStore: PageState) => getDynPages(pageStore),
  (dynPages: DynamicPagesType) => {
    return dynPages.reduce<Record<string, string>>((acc, page) => {
      if (page.dynCollection) {
        acc[page.dynCollection] = page.id;
      }
      return acc;
    }, {});
  }
);

export type ProtectedPageNodeType = 'passwordProtected' | 'userGroup' | null;

/**
 * Returns protected type for page or folder
 */
export const getProtectedType = (
  page: PageRecordType | null
): ProtectedPageNodeType => {
  if (!page) {
    return null;
  }

  const isPasswordProtected = page.get('passwordProtected');
  const isUserGroupProtected = Boolean(page.get('groupAccess')?.size);

  if (isPasswordProtected) {
    return 'passwordProtected';
  }

  if (isUserGroupProtected) {
    return 'userGroup';
  }

  return null;
};
